import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ButtonsContainerComponent,
  FileUploadToStorageComponent,
  isDisabled$,
  IUploadStartedEvent,
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { Brand, Practice } from '@principle-theorem/principle-core';
import {
  IPractice,
  IPracticeAccSettings,
  ITemplateDefinition,
  TemplateScope,
  TemplateType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  isSameRef,
  multiFilter,
  ONLY_NUMBERS,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { OrganisationService } from '../../../organisation.service';
import { InformationBoxComponent } from '../../information-box/information-box.component';

@Component({
  selector: 'pr-practice-acc-settings',
  imports: [
    NgCommonModule,
    MatInputModule,
    MatButton,
    MatSlideToggle,
    MatSelectModule,
    MatIcon,
    FileUploadToStorageComponent,
    InformationBoxComponent,
    ButtonsContainerComponent,
  ],
  templateUrl: './practice-acc-settings.component.html',
  styleUrl: './practice-acc-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeAccSettingsComponent {
  private _organisation = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);

  practice = input.required<WithRef<IPractice>>();
  accSettings = computed(() => this.practice().accSettings);
  storagePath = computed(() => Practice.accStoragePath(this.practice()));
  formSaved = output();
  saveDisabled$: Observable<boolean>;
  readonly pfxExtensions = ['.pfx', '.p12'];

  patientEmailTemplates$: Observable<WithRef<ITemplateDefinition>[]>;
  patientSmsTemplates$: Observable<WithRef<ITemplateDefinition>[]>;

  form = new TypedFormGroup<IPracticeAccSettings>({
    enabled: new TypedFormControl<boolean>(false),
    testMode: new TypedFormControl<boolean>(false),
    addACCAsPending: new TypedFormControl<boolean>(false),
    vendorId: new TypedFormControl<string>('', [
      Validators.required,
      Validators.minLength(6),
    ]),
    providerId: new TypedFormControl<string>('', [Validators.minLength(6)]),
    facilityId: new TypedFormControl<string>(''),
    pfxPassphrase: new TypedFormControl<string>(''),
    pfxFilePath: new TypedFormControl<string>(''),
    vendorGST: new TypedFormControl<string>(
      '',
      Validators.pattern(ONLY_NUMBERS)
    ),
    formNotificationSmsTemplate: new TypedFormControl<
      DocumentReference<ITemplateDefinition> | undefined
    >(undefined),
    formNotificationEmailTemplate: new TypedFormControl<
      DocumentReference<ITemplateDefinition> | undefined
    >(undefined),
  });

  constructor() {
    effect(() => {
      const accSettings = this.accSettings();
      if (!accSettings) {
        return;
      }
      this.form.patchValue(accSettings);
    });

    this.saveDisabled$ = isDisabled$(this.form);

    const patientScopeTemplates$ = this._organisation.brand$.pipe(
      filterUndefined(),
      switchMap((brand) => Brand.templates$(brand)),
      multiFilter((template) => template.scope === TemplateScope.Patient)
    );

    this.patientEmailTemplates$ = patientScopeTemplates$.pipe(
      multiFilter((template) => template.type === TemplateType.Html)
    );

    this.patientSmsTemplates$ = patientScopeTemplates$.pipe(
      multiFilter((template) => template.type === TemplateType.PlainText)
    );
  }

  async uploadPfxFile(event: IUploadStartedEvent): Promise<void> {
    await event.task;
    this._snackBar.open('PFX File Uploaded');
    this.form.controls.pfxFilePath.setValue(event.storagePath);
  }

  notifyPfxFileRejected(): void {
    this._snackBar.open(
      'That file is not a certificate. Upload the .pfx or .p12 file from HealthLink.'
    );
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const accSettings = this.form.getRawValue();
    await Firestore.patchDoc(this.practice().ref, {
      accSettings,
    });
    this._snackBar.open('ACC Settings Saved');
    this.formSaved.emit();
  }

  getPfxFileName(storagePath: string): string {
    const segments = storagePath.split('/');
    return segments[segments.length - 1];
  }

  compareTemplates(
    templateA: DocumentReference<ITemplateDefinition> | undefined,
    templateB: DocumentReference<ITemplateDefinition> | undefined
  ): boolean {
    return isSameRef(templateA, templateB);
  }
}
